import { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { reportsApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  UserCheck,
  Clock,
  Frown,
  RefreshCw,
  Download,
  DollarSign,
  Activity,
  TrendingUp,
  Gauge,
  X,
  FileText,
  Copy,
} from "lucide-react";

const VERDICT_CONFIG = {
  resolved:       { label: "Resolved",       emoji: "✅", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle2 },
  rephrased:      { label: "Rephrased",      emoji: "⚠️", bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   icon: AlertTriangle },
  "agent-takeover": { label: "Agent took over", emoji: "🔁", bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-700",    icon: UserCheck },
  "no-response":  { label: "No response",    emoji: "—",  bg: "bg-gray-50",    border: "border-gray-200",    text: "text-gray-600",    icon: Clock },
  frustrated:     { label: "Frustrated",     emoji: "❌", bg: "bg-red-50",     border: "border-red-200",     text: "text-red-700",     icon: Frown },
};

const AiQualityReportPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [sampleSize, setSampleSize] = useState(20);
  const [report, setReport] = useState(null);
  const [filterVerdict, setFilterVerdict] = useState("all");
  // Yash, Apr 29 2026 — LLM cost telemetry. Loaded in parallel with the
  // quality report so the card updates the moment the date range changes.
  const [costReport, setCostReport] = useState(null);
  const [costLoading, setCostLoading] = useState(true);
  // Revenue attributed to AI (online paid + COD) for the same range.
  const [revenueReport, setRevenueReport] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setCostLoading(true);
    try {
      const [{ data }, costRes, revRes] = await Promise.all([
        reportsApi.getAiQuality({
          start_date: startDate,
          end_date: endDate,
          sample_size: sampleSize,
        }),
        reportsApi.getLlmCost({ start_date: startDate, end_date: endDate }).catch((e) => {
          // Cost endpoint failure must not block the main quality report.
          console.warn("LLM cost report failed:", e);
          return { data: null };
        }),
        reportsApi.getLlmRevenue({ start_date: startDate, end_date: endDate }).catch((e) => {
          console.warn("LLM revenue report failed:", e);
          return { data: null };
        }),
      ]);
      setReport(data);
      setCostReport(costRes.data);
      setRevenueReport(revRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load AI quality report");
    } finally {
      setLoading(false);
      setCostLoading(false);
    }
  }, [startDate, endDate, sampleSize]);

  useEffect(() => {
    if (user?.role === "super_admin") fetchReport();
  }, [fetchReport, user]);

  // Hard guard — redirect non-super-admins
  if (user && user.role !== "super_admin") {
    return <Navigate to="/reports" replace />;
  }

  const exportCsv = () => {
    if (!report?.samples) return;
    const rows = [["Verdict", "Lead Name", "Phone", "Department", "Customer Said", "AI Replied", "Customer Next Msg", "Agent Took Over", "AI Reply At"]];
    report.samples.forEach((s) => {
      rows.push([
        s.verdict,
        s.lead_name,
        s.lead_phone,
        s.department,
        (s.customer_prev_msg || "").replace(/\n/g, " ").replace(/,/g, ";"),
        (s.ai_reply || "").replace(/\n/g, " ").replace(/,/g, ";"),
        (s.customer_next_msg || "").replace(/\n/g, " ").replace(/,/g, ";"),
        s.agent_took_over ? "yes" : "no",
        s.ai_reply_at || "",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai_quality_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const filtered = report?.samples?.filter((s) => filterVerdict === "all" || s.verdict === filterVerdict) || [];

  const totalBuckets = report?.bucket_counts || {};
  const totalSampled = report?.sample_size || 0;

  return (
    <div data-testid="ai-quality-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/reports" className="btn-icon" data-testid="back-to-reports-btn" title="Back to Reports">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-2xl font-extrabold text-[--text-primary] tracking-tight flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <Sparkles size={20} className="text-purple-600" /> AI Quality Report
            </h1>
            <p className="text-sm text-[--text-muted]">Random sample of AI auto-replies — Super Admin only</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border-2 border-[--border] rounded-full px-3 py-1.5">
            <Calendar size={14} className="text-[--text-muted]" />
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-xs border-none outline-none bg-transparent" data-testid="aiq-start-date" />
            <span className="text-xs text-[--text-muted]">to</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-xs border-none outline-none bg-transparent" data-testid="aiq-end-date" />
          </div>
          <select
            value={sampleSize}
            onChange={(e) => setSampleSize(parseInt(e.target.value, 10))}
            className="bg-white border-2 border-[--border] rounded-full px-3 py-1.5 text-xs font-semibold"
            data-testid="aiq-sample-size"
          >
            <option value={10}>10 samples</option>
            <option value={20}>20 samples</option>
            <option value={30}>30 samples</option>
            <option value={50}>50 samples</option>
          </select>
          <button onClick={fetchReport} disabled={loading} className="btn-icon" title="Refresh" data-testid="aiq-refresh-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          </button>
          <button onClick={exportCsv} disabled={!report?.samples?.length} className="btn-secondary flex items-center gap-1.5 text-xs" data-testid="aiq-export-btn">
            <Download size={13} /> CSV
          </button>
          {/* Iter 152.22 — LLM cache diagnostics modal trigger. Fetches the
              super-admin `/api/admin/llm-cache-diag` endpoint via the
              already-authenticated axios client (no more URL-in-browser
              auth hassle) and renders a readable breakdown of today's
              spend, cache hit %, and top spenders. */}
          <LlmCacheDiagButton />
          <LlmCachePromptButton />
        </div>
      </div>

      {/* LLM Cost Card — Yash, Apr 29 2026.
          Estimated token spend on Claude / OpenAI for the selected date range.
          See backend `_track_llm_call` for the math (chars/3.5 heuristic +
          flat 1500 input tokens per vision image). The "Estimated" badge is
          deliberately visible so super-admins know these are not invoiced
          numbers — they're a reasonable proxy you can sanity-check against
          the real Anthropic/OpenAI bill at month-end. */}
      <LlmCostCard report={costReport} loading={costLoading} onRefresh={fetchReport} />

      {/* Cost-vs-Revenue widget — answers the CFO question: "what's our LLM
          spend as a % of revenue?". Combines (1) paid online links on
          AI-touched leads + (2) any order logged on a lead where AI sent
          the "Order ready to place" email (sales_address_emailed_at).
          All amounts normalised to USD. */}
      <CostVsRevenueCard
        cost={costReport}
        revenue={revenueReport}
        onRefresh={fetchReport}
        startDate={startDate}
        endDate={endDate}
        onClearDateFilter={() => {
          // Iter 153.14 (Feb 8, 2026, Yash) — backfill UX. When the
          // backfill toast surfaces older orders outside the current
          // date range, the user can one-click to all-time view.
          setStartDate("");
          setEndDate("");
        }}
      />

      {/* Bucket Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="aiq-bucket-summary">
        {Object.entries(VERDICT_CONFIG).map(([key, cfg]) => {
          const count = totalBuckets[key] || 0;
          const pct = totalSampled > 0 ? Math.round((count / totalSampled) * 100) : 0;
          const Icon = cfg.icon;
          const isActive = filterVerdict === key;
          return (
            <button
              key={key}
              onClick={() => setFilterVerdict(isActive ? "all" : key)}
              className={`${cfg.bg} ${cfg.border} ${cfg.text} ${isActive ? "ring-2 ring-offset-1" : ""} border-2 rounded-xl p-3 text-left transition-all hover:scale-[1.02]`}
              data-testid={`aiq-bucket-${key}`}
              title={`Click to filter by ${cfg.label}`}
            >
              <div className="flex items-center justify-between mb-1">
                <Icon size={16} />
                <span className="text-[10px] font-bold opacity-60">{cfg.emoji}</span>
              </div>
              <div className="text-2xl font-extrabold">{count}</div>
              <div className="text-[11px] font-semibold opacity-80">{cfg.label} · {pct}%</div>
            </button>
          );
        })}
      </div>

      {/* Meta */}
      {report && (
        <div className="bg-white rounded-xl border border-[--border] p-3 text-xs text-[--text-muted] flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="aiq-meta">
          <span>Sampled <strong className="text-[--text-primary]">{report.sample_size}</strong> of <strong className="text-[--text-primary]">{report.total_ai_replies_in_range}</strong> AI replies</span>
          <span>Range: {startDate} → {endDate}</span>
          {filterVerdict !== "all" && (
            <button onClick={() => setFilterVerdict("all")} className="ml-auto text-[11px] font-bold text-[--accent] hover:underline" data-testid="aiq-clear-filter">
              Clear filter ({filterVerdict})
            </button>
          )}
        </div>
      )}

      {/* Samples list */}
      {loading && !report ? (
        <div className="flex items-center justify-center h-64 text-[--text-muted]">
          <Loader2 size={24} className="animate-spin mr-2" /> Loading AI samples…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-[--border] p-8 text-center text-[--text-muted]" data-testid="aiq-empty">
          <Sparkles size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No AI replies found for this range / filter.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="aiq-samples-list">
          {filtered.map((s) => {
            const cfg = VERDICT_CONFIG[s.verdict] || VERDICT_CONFIG["no-response"];
            return (
              <div key={s.ai_msg_id} className="bg-white rounded-xl border border-[--border] p-4 hover:shadow-md transition-shadow" data-testid={`aiq-sample-${s.ai_msg_id}`}>
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`${cfg.bg} ${cfg.border} ${cfg.text} border rounded-full px-2.5 py-1 text-[11px] font-bold flex items-center gap-1`}>
                      {cfg.emoji} {cfg.label}
                    </span>
                    <Link to={`/chats?lead=${s.lead_id}`} className="text-sm font-bold text-[--text-primary] hover:text-[--accent] hover:underline" data-testid={`aiq-sample-lead-${s.ai_msg_id}`}>
                      {s.lead_name}
                    </Link>
                    <span className="text-[11px] text-[--text-muted]">+{s.lead_phone}</span>
                    {s.department && <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-semibold">{s.department}</span>}
                    {s.category && <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">{s.category}</span>}
                  </div>
                  <span className="text-[11px] text-[--text-muted]">{fmtDateTime(s.ai_reply_at)}</span>
                </div>

                {/* Customer's prior message */}
                <div className="mb-2" data-testid={`aiq-prev-${s.ai_msg_id}`}>
                  <div className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider mb-1">Customer asked</div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-[13px] text-[--text-primary] whitespace-pre-wrap">
                    {s.customer_prev_msg || <span className="text-[--text-muted] italic">— no prior message —</span>}
                  </div>
                </div>

                {/* AI reply */}
                <div className="mb-2" data-testid={`aiq-reply-${s.ai_msg_id}`}>
                  <div className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-1 flex items-center gap-1"><Sparkles size={10} /> AI replied</div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 text-[13px] text-[--text-primary] whitespace-pre-wrap">
                    {s.ai_reply}
                  </div>
                </div>

                {/* Customer's next message (the signal) */}
                <div data-testid={`aiq-next-${s.ai_msg_id}`}>
                  <div className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider mb-1">Customer responded</div>
                  <div className={`${s.customer_next_msg ? "bg-gray-50 border-gray-200" : "bg-gray-50/50 border-dashed border-gray-200"} border rounded-lg p-2.5 text-[13px] text-[--text-primary] whitespace-pre-wrap`}>
                    {s.customer_next_msg || <span className="text-[--text-muted] italic">— no follow-up within 72h —</span>}
                    {s.customer_next_msg && (
                      <span className="block text-[10px] text-[--text-muted] mt-1">{fmtDateTime(s.customer_next_msg_at)}</span>
                    )}
                  </div>
                </div>

                {s.agent_took_over && (
                  <div className="mt-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5" data-testid={`aiq-agent-${s.ai_msg_id}`}>
                    <UserCheck size={12} /> Agent replied within 2h at {fmtDateTime(s.agent_took_over_at)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AiQualityReportPage;

/**
 * LlmCostCard — at-a-glance LLM spend for the selected date range.
 *
 * Shows: total estimated USD cost, total calls, total tokens (input/output
 * split), and a per-purpose breakdown bar (auto-reply vs vision OCR vs
 * prefill) so super-admins immediately see where the money is going.
 *
 * The numbers are ESTIMATES (chars/3.5 + 1500 tokens/vision-image average)
 * and clearly labelled as such — the real source of truth is the Anthropic
 * / OpenAI invoice. We expect to be within ±15% of the real bill.
 */
const PURPOSE_META = {
  ai_auto_reply:  { label: "Auto-reply (Claude)",  color: "violet" },
  ai_draft:       { label: "Draft suggestion",     color: "indigo" },
  verify_payment: { label: "Verify payment image", color: "emerald" },
  prefill_ocr:    { label: "Prefill OCR (vision)", color: "amber" },
  prefill_extract:{ label: "Prefill extraction",   color: "fuchsia" },
};

const COLOR_CLASS = {
  violet:   { bg: "bg-violet-100",   text: "text-violet-800",   bar: "bg-violet-500" },
  indigo:   { bg: "bg-indigo-100",   text: "text-indigo-800",   bar: "bg-indigo-500" },
  emerald:  { bg: "bg-emerald-100",  text: "text-emerald-800",  bar: "bg-emerald-500" },
  amber:    { bg: "bg-amber-100",    text: "text-amber-800",    bar: "bg-amber-500" },
  fuchsia:  { bg: "bg-fuchsia-100",  text: "text-fuchsia-800",  bar: "bg-fuchsia-500" },
  slate:    { bg: "bg-slate-100",    text: "text-slate-800",    bar: "bg-slate-500" },
};

const formatUsd = (n) => `$${Number(n || 0).toFixed(4)}`;
const formatTok = (n) => Number(n || 0).toLocaleString();

const LlmCostCard = ({ report, loading, onRefresh }) => {
  const [backfilling, setBackfilling] = useState(false);

  // Yash, Apr 29 2026 — one-shot historical backfill. Scans every
  // is_ai_reply message ever sent and inserts a back-dated row in
  // llm_usage_log with the per-message output-token-accurate cost
  // (input tokens use the measured average from live tracking).
  // Idempotent — Mongo-side dedupe by backfill_message_id.
  const runBackfill = async () => {
    if (backfilling) return;
    if (!window.confirm(
      "This will scan every historical AI reply and add a back-dated cost row for each.\n\n" +
      "It's safe to re-run (idempotent), but takes ~30-60 seconds.\n\nProceed?",
    )) return;
    setBackfilling(true);
    try {
      const { data } = await reportsApi.backfillLlmCost();
      toast.success(
        `Backfill complete. Added ${data.inserted} historical rows · $${data.cost_added_usd} cost (using avg ${data.avg_input_tokens_used.toLocaleString()} input tokens).`,
        { duration: 8000 },
      );
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed — try again.");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading && !report) {
    return (
      <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-2xl p-4 flex items-center justify-center text-violet-600 text-sm" data-testid="llm-cost-loading">
        <Loader2 size={16} className="animate-spin mr-2" /> Calculating LLM spend…
      </div>
    );
  }
  if (!report) return null;
  const summary = report.summary || {};
  const lifetime = report.lifetime || { calls: 0, cost: 0 };
  const byPurpose = (report.by_purpose || []).filter((p) => Number(p.cost) > 0);
  const total = Number(summary.cost || 0);
  const trackingStart = report.tracking_started_at
    ? new Date(report.tracking_started_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div
      className="bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border-2 border-violet-300 rounded-2xl p-5 space-y-4 shadow-sm"
      data-testid="llm-cost-card"
    >
      {/* Hero — date-range total + lifetime total side by side. */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow">
            <DollarSign size={20} />
          </div>
          <div>
            <p className="text-[16px] font-extrabold text-violet-900 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Total LLM spend
            </p>
            <p className="text-[10.5px] text-violet-600 font-bold leading-tight uppercase tracking-wider">
              For selected date range · all calls counted
            </p>
            <p className="text-[10px] text-violet-500 mt-0.5 italic">
              Independent of the “samples” selector → counts every Claude/OpenAI call.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-8 ml-auto">
          <div className="text-right">
            <p className="text-[9px] font-extrabold uppercase tracking-wider text-violet-500">In date range</p>
            <p className="text-[34px] font-extrabold text-violet-900 leading-none font-mono" data-testid="llm-cost-total">
              {formatUsd(total)}
            </p>
            <p className="text-[10px] text-violet-600 font-bold mt-1" data-testid="llm-cost-calls">
              {summary.calls || 0} calls
            </p>
          </div>
          <div className="text-right border-l border-violet-200 pl-4 sm:pl-8">
            <p className="text-[9px] font-extrabold uppercase tracking-wider text-violet-500">Since tracking</p>
            <p className="text-[20px] font-extrabold text-violet-700 leading-none font-mono" data-testid="llm-cost-lifetime">
              {formatUsd(lifetime.cost)}
            </p>
            <p className="text-[10px] text-violet-500 mt-1" data-testid="llm-cost-lifetime-calls">
              {lifetime.calls || 0} calls total
            </p>
          </div>
        </div>
      </div>

      {/* Token totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10.5px]">
        <CostStat label="Input tokens" value={formatTok(summary.input_tokens)} icon={Activity} />
        <CostStat label="Output tokens" value={formatTok(summary.output_tokens)} icon={Activity} />
        <CostStat label="Vision images" value={formatTok(summary.image_count)} icon={Sparkles} />
        <CostStat label="Avg / call" value={summary.calls > 0 ? formatUsd(total / summary.calls) : "—"} icon={DollarSign} />
      </div>

      {/* Per-purpose breakdown bar */}
      {byPurpose.length > 0 && total > 0 && (
        <div className="space-y-1.5" data-testid="llm-cost-by-purpose">
          <div className="flex h-2 w-full rounded-full overflow-hidden border border-violet-100">
            {byPurpose.map((p) => {
              const meta = PURPOSE_META[p.purpose] || { label: p.purpose, color: "slate" };
              const cls = COLOR_CLASS[meta.color] || COLOR_CLASS.slate;
              const w = (Number(p.cost) / total) * 100;
              return (
                <div
                  key={p.purpose}
                  className={cls.bar}
                  style={{ width: `${w}%` }}
                  title={`${meta.label}: ${formatUsd(p.cost)} (${w.toFixed(1)}%)`}
                />
              );
            })}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {byPurpose.map((p) => {
              const meta = PURPOSE_META[p.purpose] || { label: p.purpose, color: "slate" };
              const cls = COLOR_CLASS[meta.color] || COLOR_CLASS.slate;
              const w = (Number(p.cost) / total) * 100;
              return (
                <div
                  key={p.purpose}
                  className={`${cls.bg} ${cls.text} text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1`}
                  data-testid={`llm-cost-purpose-${p.purpose}`}
                  title={`${p.calls} calls · ${formatTok(p.input_tokens)} in / ${formatTok(p.output_tokens)} out`}
                >
                  {meta.label} · {formatUsd(p.cost)}
                  <span className="opacity-60">({w.toFixed(0)}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-2 border-t border-violet-100 pt-2">
        <p className="text-[10px] text-violet-600 leading-snug flex-1">
          <span className="font-bold">Methodology:</span> token counts via <span className="font-mono bg-white/60 px-1 rounded">tiktoken cl100k_base</span>{" "}
          (±5% vs. Anthropic, exact for OpenAI) + flat 1,500 input-tokens per vision image.
          Pricing: Claude Sonnet 4.5 $3/$15 per 1M, Haiku $0.80/$4 per 1M, GPT-4o-mini $0.15/$0.60 per 1M.
          {trackingStart && (
            <> Tracking started <span className="font-bold">{trackingStart}</span>.</>
          )}
        </p>
        <button
          type="button"
          onClick={runBackfill}
          disabled={backfilling}
          title="Scan every historical AI reply and add the cost into the lifetime total. Safe to re-run."
          className="text-[10px] font-bold text-violet-700 bg-white border border-violet-300 rounded-lg px-2.5 py-1.5 hover:bg-violet-50 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1 whitespace-nowrap"
          data-testid="llm-cost-backfill-btn"
        >
          {backfilling ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {backfilling ? "Backfilling…" : "Backfill historical spend"}
        </button>
      </div>
    </div>
  );
};

const CostStat = ({ label, value, icon: Icon }) => (
  <div className="bg-white/80 border border-violet-100 rounded-lg p-2 flex items-start gap-1.5">
    {Icon && <Icon size={11} className="text-violet-400 mt-0.5 shrink-0" />}
    <div className="min-w-0">
      <p className="text-violet-500 font-bold uppercase tracking-wider text-[9px] leading-tight truncate">{label}</p>
      <p className="font-mono font-extrabold text-violet-900 text-[12px] leading-tight">{value}</p>
    </div>
  </div>
);

/**
 * CostVsRevenueCard — "what did the AI earn us vs. what did it cost us?"
 *
 * Pulls revenue from /reports/llm-revenue (paid online links + COD admin
 * orders on AI-touched leads, all in USD) and pairs it with the date-range
 * cost from /reports/llm-cost. Prints the ratio that the CFO actually wants:
 *   "we spent $X on Claude → drove $Y in confirmed revenue → ROI multiple"
 *
 * Why two cards instead of merging into LlmCostCard? Cost is the obvious
 * super-admin question ("how much are we paying?"). Revenue answers a
 * different one ("was it worth it?") and deserves its own visual weight.
 */
const CostVsRevenueCard = ({ cost, revenue, onRefresh, startDate, endDate, onClearDateFilter }) => {
  const [backfilling, setBackfilling] = useState(false);
  // Iter 153.14 — also fetch all-time revenue so we can always display
  // "in-range vs all-time" on the card. Without this, when the page is
  // filtered to last-7-days but synthesised orders are weeks old, the
  // user has no idea those older orders exist on the dashboard.
  const [allTimeRevenue, setAllTimeRevenue] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await reportsApi.getLlmRevenue();  // no dates → all-time
        if (!cancelled) setAllTimeRevenue(data);
      } catch (e) {
        // Best-effort; the in-range card still works.
        console.warn("All-time revenue fetch failed:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [revenue]);  // re-fetch when in-range refreshes (e.g. after backfill)

  // Iter 152.21 (May 4, 2026, Yash) — historical attribution backfill.
  // Scans leads where AI sent "Order ready to place" email but the
  // sales_address_emailed_at flag was not yet recorded (pre-Feb-2026).
  // Sets the flag retroactively → strong-signal attribution applies →
  // dashboard numbers become more accurate. Idempotent, safe to re-run.
  // Iter 153.7 (Feb 8, 2026, Yash) — pass the page's date range so the
  // toast number matches what the card shows. Previously the toast
  // showed all-time revenue and the card was filtered to last-7-days,
  // so the button looked broken when synthesised orders were older.
  const runRevenueBackfill = async () => {
    if (backfilling) return;
    if (!window.confirm(
      "This will scan historical leads and retroactively flag any whose 'Order ready to place' email predates the attribution flag.\n\n" +
      "Idempotent — safe to re-run anytime.\n\nProceed?",
    )) return;
    setBackfilling(true);
    try {
      const { data } = await reportsApi.backfillLlmRevenueAttribution({
        start_date: startDate,
        end_date: endDate,
      });
      const inRange = Number(data.revenue_after_backfill_in_range?.total_usd ?? 0);
      const allTime = Number(data.revenue_after_backfill?.total_usd ?? 0);
      const synthesised = data.auto_placed_orders_synthesized || 0;
      const flagged = data.leads_flagged || 0;
      const sameNumber = Math.abs(inRange - allTime) < 0.01;
      // Iter 153.14 (Feb 8, 2026, Yash) — when the all-time number is
      // higher than the in-range number, surface a one-click "Show
      // all-time" action on the toast so the user doesn't have to
      // hunt for the date filter.
      if (sameNumber || !onClearDateFilter) {
        toast.success(
          `Backfill complete. ${flagged} leads flagged · ${synthesised} historical orders synthesised · revenue now $${inRange.toFixed(2)}.`,
          { duration: 8000 },
        );
      } else {
        toast.success(
          `Backfill complete. ${flagged} leads flagged · ${synthesised} historical orders synthesised. ` +
          `This date range: $${inRange.toFixed(2)} · All-time: $${allTime.toFixed(2)}.`,
          {
            duration: 12000,
            action: {
              label: `Show all-time ($${allTime.toFixed(2)})`,
              onClick: () => onClearDateFilter(),
            },
          },
        );
      }
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revenue backfill failed — try again.");
    } finally {
      setBackfilling(false);
    }
  };

  // Iter 153.14 — derive whether all-time revenue exceeds in-range
  // (i.e. there are synthesised/historical orders outside the page's
  // current date filter). Surface this on the card so users see the
  // discrepancy without needing to click backfill.
  const inRangeUsd = Number(revenue?.total_usd ?? 0);
  const allTimeUsd = Number(allTimeRevenue?.total_usd ?? 0);
  const allTimeOrders = Number(allTimeRevenue?.total_count ?? 0);
  const hasOlderRevenue = allTimeUsd - inRangeUsd > 0.01;
  const isFiltered = !!(startDate || endDate);

  if (!cost || !revenue) return null;
  const costUsd = Number(cost.summary?.cost || 0);
  const revUsd = Number(revenue.total_usd || 0);
  const roiMultiple = costUsd > 0 ? revUsd / costUsd : null;
  const costPct = revUsd > 0 ? (costUsd / revUsd) * 100 : null;
  const inrFx = Number(revenue.fx_rates_used?.USD || 85);

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 via-white to-cyan-50 border-2 border-emerald-300 rounded-2xl p-5 space-y-3 shadow-sm"
      data-testid="llm-cost-vs-revenue"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-white flex items-center justify-center shadow">
          <TrendingUp size={18} />
        </div>
        <div>
          <p className="text-[15px] font-extrabold text-emerald-900 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            AI Cost vs Revenue
          </p>
          <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">
            Online paid + AI-finalised offline orders
          </p>
        </div>
      </div>

      {/* Iter 153.14 (Feb 8, 2026, Yash) — when the page is filtered to a
          date range AND the all-time revenue is higher than the in-range
          number, surface the discrepancy with a one-click "Show all-time"
          action. Without this, users couldn't tell why backfill seemed
          to do nothing — the synthesised orders were correctly added but
          their historical timestamps fell outside the page filter. */}
      {isFiltered && hasOlderRevenue && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex items-center justify-between gap-3 text-[11px]"
          data-testid="cvr-older-revenue-banner"
        >
          <span className="text-amber-900">
            <strong>${(allTimeUsd - inRangeUsd).toFixed(2)}</strong> in earlier orders ({allTimeOrders - Number(revenue.total_count || 0)} order{allTimeOrders - Number(revenue.total_count || 0) === 1 ? "" : "s"}) fall outside this date range.
          </span>
          {onClearDateFilter && (
            <button
              onClick={onClearDateFilter}
              className="text-amber-900 font-bold hover:text-amber-950 underline whitespace-nowrap"
              data-testid="cvr-show-all-time-btn"
            >
              Show all-time (${allTimeUsd.toFixed(2)}) →
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <RevStat
          label="Spent on AI"
          value={`$${costUsd.toFixed(2)}`}
          inr={`≈ ₹${Math.round(costUsd * inrFx).toLocaleString("en-IN")}`}
          tone="violet"
          testId="cvr-cost"
        />
        <RevStat
          label="Revenue earned"
          value={`$${revUsd.toFixed(2)}`}
          inr={`≈ ₹${Math.round(revUsd * inrFx).toLocaleString("en-IN")}`}
          tone="emerald"
          testId="cvr-revenue"
        />
        <RevStat
          label={roiMultiple != null && roiMultiple >= 1 ? "ROI multiple" : "Cost % of revenue"}
          value={
            roiMultiple == null ? "—"
            : roiMultiple >= 1 ? `${roiMultiple.toFixed(1)}×`
            : `${(costPct || 0).toFixed(1)}%`
          }
          inr={revenue.total_count ? `${revenue.total_count} order${revenue.total_count === 1 ? "" : "s"}` : "no orders"}
          tone={roiMultiple == null ? "slate" : roiMultiple >= 50 ? "emerald" : roiMultiple >= 10 ? "cyan" : "amber"}
          testId="cvr-roi"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 text-[10.5px]">
        <div className="bg-white/80 border border-emerald-100 rounded-lg p-2">
          <p className="text-emerald-500 font-bold uppercase tracking-wider text-[9px]">Online paid</p>
          <p className="font-mono font-extrabold text-emerald-900 text-[12px]" data-testid="cvr-online">
            ${Number(revenue.online_paid_usd || 0).toFixed(2)}
            <span className="ml-2 text-[10px] font-bold text-emerald-600">
              {revenue.online_paid_count} link{revenue.online_paid_count === 1 ? "" : "s"}
            </span>
          </p>
          <p className="text-[9px] text-emerald-500 mt-0.5">Razorpay · Stripe · PayPal webhooks</p>
        </div>
        <div className="bg-white/80 border border-cyan-100 rounded-lg p-2">
          <p className="text-cyan-500 font-bold uppercase tracking-wider text-[9px]">AI-finalised offline orders</p>
          <p className="font-mono font-extrabold text-cyan-900 text-[12px]" data-testid="cvr-cod">
            ${Number(revenue.cod_orders_usd || 0).toFixed(2)}
            <span className="ml-2 text-[10px] font-bold text-cyan-600">
              {revenue.cod_orders_count} order{revenue.cod_orders_count === 1 ? "" : "s"}
            </span>
          </p>
          <p className="text-[9px] text-cyan-500 mt-0.5">AI sent "Order ready to place" email</p>
        </div>
      </div>

      <p className="text-[10px] text-emerald-600 leading-snug border-t border-emerald-100 pt-2">
        <span className="font-bold">How it's counted:</span> a sale qualifies if Claude replied at least once on the lead{" "}
        <span className="italic">before</span> the link/order was created. Currencies normalised to USD via the saved FX
        rates table. {roiMultiple != null && roiMultiple >= 1 && (
          <span className="font-bold text-emerald-700">
            Every $1 spent on AI returned ${roiMultiple.toFixed(2)} in confirmed revenue.
          </span>
        )}
      </p>

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-emerald-100">
        <p className="text-[10px] text-emerald-600">
          Older leads missing the new attribution flag? Use this to backfill.
        </p>
        <button
          onClick={runRevenueBackfill}
          disabled={backfilling}
          data-testid="cvr-backfill-btn"
          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 disabled:cursor-not-allowed text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition"
        >
          {backfilling ? (
            <>
              <Loader2 size={12} className="animate-spin" /> Backfilling…
            </>
          ) : (
            <>Backfill historical orders</>
          )}
        </button>
      </div>
    </div>
  );
};

const RevStat = ({ label, value, inr, tone = "emerald", testId }) => {
  const cls = {
    violet:  "from-violet-50 to-violet-100 border-violet-200 text-violet-900",
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    cyan:    "from-cyan-50 to-cyan-100 border-cyan-200 text-cyan-900",
    amber:   "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    slate:   "from-slate-50 to-slate-100 border-slate-200 text-slate-700",
  }[tone];
  return (
    <div className={`bg-gradient-to-br ${cls} border rounded-xl p-3`} data-testid={testId}>
      <p className="text-[9px] font-bold uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-[22px] font-extrabold leading-none font-mono mt-1">{value}</p>
      <p className="text-[10px] mt-1 opacity-70">{inr}</p>
    </div>
  );
};


/**
 * LlmCacheDiagButton — Iter 152.22
 *
 * Opens a modal that renders the JSON output of
 * `GET /api/admin/llm-cache-diag` in a readable, annotated form.
 * Super-admin only (the API already gates on role).
 */
const LlmCacheDiagButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await reportsApi.getLlmCacheDiag();
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  };

  const openModal = async () => {
    setOpen(true);
    if (!data) await load();
  };

  return (
    <>
      <button
        onClick={openModal}
        className="btn-secondary flex items-center gap-1.5 text-xs"
        title="LLM cache diagnostics — today's spend, cache hit %, top spenders"
        data-testid="llm-cache-diag-btn"
      >
        <Gauge size={13} /> Cache Diag
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="llm-cache-diag-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="llm-cache-diag-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Gauge size={18} className="text-violet-600" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  LLM Cache Diagnostics
                </h2>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-semibold">
                  super-admin
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={load}
                  disabled={loading}
                  className="btn-icon"
                  title="Reload"
                  data-testid="llm-cache-diag-refresh"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="btn-icon"
                  title="Close"
                  data-testid="llm-cache-diag-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
              {loading && (
                <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
                  <Loader2 size={18} className="animate-spin" /> Loading diagnostics…
                </div>
              )}
              {err && !loading && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-xs" data-testid="llm-cache-diag-error">
                  <strong>Failed to load:</strong> {err}
                </div>
              )}
              {data && !loading && <LlmCacheDiagBody data={data} />}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
              <span>
                Snapshot: {data?.snapshot_at ? new Date(data.snapshot_at).toLocaleString("en-IN") : "—"}
              </span>
              <span>Read-only · no writes · safe to refresh</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * LlmCachePromptButton — Iter 153.9 (Feb 8, 2026, Yash)
 *
 * Opens a modal that fetches `GET /api/admin/llm-cache-prompt` and renders:
 *   • Per-section char/token breakdown (sortable by size)
 *   • Cost-per-call at current Sonnet 4.5 pricing
 *   • The full cached prompt text with copy-to-clipboard + download
 *
 * Use: spot bloated sections, decide what to trim. Editing requires a
 * code change to AI_AUTO_REPLY_RULES in server.py — this is read-only.
 */
const LlmCachePromptButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showFull, setShowFull] = useState(false);
  const [sortBySize, setSortBySize] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await reportsApi.getLlmCachePrompt();
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load cached prompt");
    } finally {
      setLoading(false);
    }
  };

  const openModal = async () => {
    setOpen(true);
    if (!data) await load();
  };

  const copyPrompt = async () => {
    if (!data?.prompt) return;
    try {
      await navigator.clipboard.writeText(data.prompt);
      toast.success("Cached prompt copied to clipboard");
    } catch {
      toast.error("Couldn't copy — your browser may need permission. Use Download instead.");
    }
  };

  const downloadPrompt = () => {
    if (!data?.prompt) return;
    const blob = new Blob([data.prompt], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai_auto_reply_rules_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sections = data?.sections || [];
  const sortedSections = sortBySize
    ? [...sections].sort((a, b) => b.chars - a.chars)
    : sections;
  const stats = data?.stats || {};

  return (
    <>
      <button
        onClick={openModal}
        className="btn-secondary flex items-center gap-1.5 text-xs"
        title="View the exact prompt cached on Anthropic + per-section char/token breakdown"
        data-testid="llm-cache-prompt-btn"
      >
        <FileText size={13} /> Cached Prompt
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="llm-cache-prompt-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="llm-cache-prompt-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-violet-600" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Cached Prompt — AI_AUTO_REPLY_RULES
                </h2>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-semibold">
                  super-admin · read-only
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyPrompt}
                  disabled={!data?.prompt}
                  className="btn-icon"
                  title="Copy full prompt to clipboard"
                  data-testid="llm-cache-prompt-copy"
                >
                  <Copy size={14} />
                </button>
                <button
                  onClick={downloadPrompt}
                  disabled={!data?.prompt}
                  className="btn-icon"
                  title="Download as .txt"
                  data-testid="llm-cache-prompt-download"
                >
                  <Download size={14} />
                </button>
                <button
                  onClick={load}
                  disabled={loading}
                  className="btn-icon"
                  title="Reload"
                  data-testid="llm-cache-prompt-refresh"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="btn-icon"
                  title="Close"
                  data-testid="llm-cache-prompt-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
              {loading && (
                <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
                  <Loader2 size={18} className="animate-spin" /> Loading cached prompt…
                </div>
              )}
              {err && !loading && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-xs" data-testid="llm-cache-prompt-error">
                  <strong>Failed to load:</strong> {err}
                </div>
              )}
              {data && !loading && (
                <>
                  {/* Stats strip */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="llm-cache-prompt-stats">
                    <Stat
                      label="Total size"
                      value={`${stats.total_chars?.toLocaleString() || 0} chars`}
                      sub={`~${stats.approx_tokens?.toLocaleString() || 0} tokens`}
                      tone="violet"
                    />
                    <Stat
                      label="Cache write (1h TTL)"
                      value={`$${stats.cache_write_cost_per_call_usd ?? 0}`}
                      sub="per uncached call"
                      tone="rose"
                    />
                    <Stat
                      label="Cache read (hit)"
                      value={`$${stats.cache_read_cost_per_call_usd ?? 0}`}
                      sub="per cached call"
                      tone="emerald"
                    />
                    <Stat
                      label="Sections"
                      value={stats.section_count || 0}
                      sub="logical blocks"
                      tone="slate"
                    />
                  </div>

                  {/* Section breakdown */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                        Section breakdown
                      </span>
                      <button
                        onClick={() => setSortBySize((s) => !s)}
                        className="text-[10px] text-violet-700 hover:text-violet-900 font-semibold"
                        data-testid="llm-cache-prompt-sort-toggle"
                      >
                        Sorted by: {sortBySize ? "size ↓" : "document order"} (click to toggle)
                      </button>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-semibold">#</th>
                          <th className="px-3 py-1.5 text-left font-semibold">Section</th>
                          <th className="px-3 py-1.5 text-right font-semibold">Chars</th>
                          <th className="px-3 py-1.5 text-right font-semibold">~Tokens</th>
                          <th className="px-3 py-1.5 text-right font-semibold">% of total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSections.map((sec, idx) => {
                          const pct = stats.total_chars
                            ? ((sec.chars / stats.total_chars) * 100).toFixed(1)
                            : 0;
                          const heavy = sec.chars > 3000;
                          return (
                            <tr
                              key={sec.title}
                              className={`border-t border-slate-100 ${heavy ? "bg-amber-50/40" : ""}`}
                              data-testid={`llm-cache-prompt-row-${idx}`}
                            >
                              <td className="px-3 py-1.5 text-slate-400 font-mono">{idx + 1}</td>
                              <td className="px-3 py-1.5 font-medium text-slate-800">
                                {sec.title}
                                {heavy && (
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold">
                                    HEAVY
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono text-slate-700">
                                {sec.chars.toLocaleString()}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono text-slate-500">
                                {sec.approx_tokens.toLocaleString()}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono text-slate-500">
                                {pct}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Full prompt — collapsed by default */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setShowFull((v) => !v)}
                      className="w-full bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200 hover:bg-slate-100 transition"
                      data-testid="llm-cache-prompt-toggle-full"
                    >
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                        Full prompt text
                      </span>
                      <span className="text-[10px] text-slate-500">
                        {showFull ? "Hide" : "Show"} ({stats.total_chars?.toLocaleString() || 0} chars)
                      </span>
                    </button>
                    {showFull && (
                      <pre
                        className="p-3 text-[11px] leading-snug text-slate-800 whitespace-pre-wrap font-mono bg-slate-900/[0.02] max-h-96 overflow-y-auto"
                        data-testid="llm-cache-prompt-full-text"
                      >
                        {data.prompt}
                      </pre>
                    )}
                  </div>

                  {data.note && (
                    <p className="text-[11px] text-slate-500 italic">{data.note}</p>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
              <span>
                Snapshot: {data?.snapshot_at ? new Date(data.snapshot_at).toLocaleString("en-IN") : "—"}
              </span>
              <span>Editing requires code change · cache invalidates once</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Tiny stat tile — reused inside the prompt-stats strip.
const Stat = ({ label, value, sub, tone = "slate" }) => {
  const tones = {
    violet:  "border-violet-200 bg-violet-50 text-violet-900",
    rose:    "border-rose-200 bg-rose-50 text-rose-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    slate:   "border-slate-200 bg-slate-50 text-slate-900",
  };
  return (
    <div className={`border rounded-lg p-2.5 ${tones[tone] || tones.slate}`}>
      <p className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-[15px] font-extrabold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
        {value}
      </p>
      {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
};

const LlmCacheDiagBody = ({ data }) => {
  const today = data.total_today || data.total_24h || {};
  const env = data.env || {};
  const tel = data.telemetry || {};
  const cacheHitPct = data.direct_anthropic_cache_hit_pct_24h;
  const cacheSavings = data.cache_savings_24h;
  const byPurpose = data.by_purpose_today || data.by_purpose_24h || [];
  const autoReply = data.ai_auto_reply_24h_by_source || [];
  const prewarmer = data.prewarmer;

  const budgetPct = today.budget_cap_usd
    ? Math.min(100, Math.round((today.spend_usd / today.budget_cap_usd) * 100))
    : 0;

  // Iter 152.22c — pre-warmer health chip. Healthy if it fired in the
  // last 60 min (intended interval = 50 min). Stale if > 90 min.
  // Iter 152.22i (May 2026, Yash) — pre-warmer now runs 24/7 (was 8am-9pm IST).
  // The "sleeping" state is no longer rendered; backend always sends
  // `in_business_window=true`.
  const fmtAge = (seconds) => {
    if (seconds == null) return "unknown";
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return `${h}h ${m}m ago`;
  };
  const prewarmerHealth = prewarmer
    ? prewarmer.seconds_since_last == null
      ? "unknown"
      : prewarmer.seconds_since_last <= 60 * 60
        ? "healthy"
        : prewarmer.seconds_since_last <= 90 * 60
          ? "warm"
          : "stale"
    : "missing";
  const prewarmerCls = {
    healthy: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warm: "bg-amber-50 border-amber-200 text-amber-800",
    stale: "bg-rose-50 border-rose-200 text-rose-800",
    missing: "bg-slate-100 border-slate-300 text-slate-600",
    unknown: "bg-slate-50 border-slate-200 text-slate-500",
  }[prewarmerHealth];
  const prewarmerIcon = {
    healthy: "🔥",
    warm: "🌤",
    stale: "⚠",
    missing: "—",
    unknown: "?",
  }[prewarmerHealth];

  return (
    <div className="space-y-4" data-testid="llm-cache-diag-body">
      {/* Pre-warmer status chip — iter 152.22c / i (24/7) */}
      <div
        className={`flex flex-wrap items-center gap-3 rounded-xl px-4 py-2.5 text-xs border ${prewarmerCls}`}
        data-testid="llm-cache-prewarmer-chip"
        data-health={prewarmerHealth}
      >
        <span className="text-sm">{prewarmerIcon}</span>
        <span className="font-bold uppercase tracking-wider text-[10px]">Cache pre-warmer</span>
        <span className="flex-1 min-w-[200px]">
          {prewarmer ? (
            <>
              Last fired <span className="font-mono font-semibold">{fmtAge(prewarmer.seconds_since_last)}</span>
              {" · "}
              <span className="font-mono">{prewarmer.fires_today} fires today</span>
              {" · cache_read="}
              <span className="font-mono">{(prewarmer.last_cache_read_tokens || 0).toLocaleString()}</span>
            </>
          ) : (
            <>Never fired · verify <code className="font-mono">IS_PREVIEW=0</code> on prod, check backend logs for <code className="font-mono">[cache-prewarmer]</code></>
          )}
        </span>
        <span className="font-mono text-[10px] opacity-70 hidden sm:inline">every 50 min · 24/7</span>
        <JumpstartPrewarmerButton />
      </div>

      {/* Top row: today's spend + cache hit % + savings + env */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className={`rounded-xl p-4 border ${today.killswitch_triggered ? "bg-rose-50 border-rose-200" : budgetPct >= 80 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">Today's spend · IST</p>
          <p className="text-2xl font-extrabold font-mono mt-1">${(today.spend_usd || 0).toFixed(2)}</p>
          <p className="text-[11px] mt-1 opacity-80">
            of ${(today.budget_cap_usd || 0).toFixed(0)} cap · {today.calls || 0} calls
            {today.override_active && (
              <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold" title={`Override by ${today.override_set_by || 'admin'}`}>
                override
              </span>
            )}
          </p>
          <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
            <div
              className={`h-full ${today.killswitch_triggered ? "bg-rose-500" : budgetPct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
              style={{ width: `${budgetPct}%` }}
            />
          </div>
          {today.killswitch_triggered && (
            <p className="text-[10px] font-bold text-rose-700 mt-2">⚠ Kill-switch ACTIVE — auto-reply paused until IST midnight</p>
          )}
          {/* Iter 152.22k — Resume AI / override controls */}
          <BudgetOverrideControls today={today} />
        </div>

        <div className="rounded-xl p-4 border bg-violet-50 border-violet-200">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">Cache hit % · 24h</p>
          <p className="text-2xl font-extrabold font-mono mt-1 text-violet-900">
            {cacheHitPct === null || cacheHitPct === undefined ? "—" : `${cacheHitPct}%`}
          </p>
          <p className="text-[11px] mt-1 opacity-80">
            {cacheHitPct >= 50 ? "Healthy — 1h TTL is working" : cacheHitPct === null ? "No direct-Anthropic calls yet" : "Low — check anthropic-beta header"}
          </p>
        </div>

        {/* Iter 152.22j — explicit cache savings widget. Answers "are we
            actually saving money?" with a dollar figure and monthly
            projection. */}
        {cacheSavings ? (
          <div className="rounded-xl p-4 border bg-teal-50 border-teal-200" data-testid="llm-cache-savings-card">
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 text-teal-900">Cache savings · 24h</p>
            <p className="text-2xl font-extrabold font-mono mt-1 text-teal-900" title={cacheSavings.explanation}>
              ${cacheSavings.saved_usd_24h.toFixed(2)}
              <span className="text-[11px] font-bold text-teal-700 ml-1">({cacheSavings.saved_pct}%)</span>
            </p>
            <p className="text-[11px] mt-1 opacity-80">
              vs no-cache ${cacheSavings.hypothetical_no_cache_cost_24h_usd.toFixed(2)}
            </p>
            <p className="text-[10px] mt-1 text-teal-700 font-semibold">
              ≈ ${cacheSavings.monthly_projection_usd.toFixed(0)}/mo saved
            </p>
          </div>
        ) : (
          <div className="rounded-xl p-4 border bg-slate-50 border-slate-200">
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">Cache savings · 24h</p>
            <p className="text-2xl font-extrabold font-mono mt-1 text-slate-500">—</p>
            <p className="text-[11px] mt-1 opacity-80">No direct-Anthropic data</p>
          </div>
        )}

        <div className="rounded-xl p-4 border bg-slate-50 border-slate-200">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">Environment</p>
          <ul className="text-xs mt-1 space-y-0.5 font-mono">
            <li>
              ANTHROPIC_API_KEY:{" "}
              <span className={env.anthropic_api_key_present ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>
                {env.anthropic_api_key_present ? `present (${env.anthropic_api_key_length}ch)` : "MISSING"}
              </span>
            </li>
            <li>
              EMERGENT_LLM_KEY:{" "}
              <span className={env.emergent_llm_key_present ? "text-emerald-700 font-bold" : "text-slate-500"}>
                {env.emergent_llm_key_present ? "present" : "missing"}
              </span>
            </li>
            <li className="text-slate-500 pt-1 border-t border-slate-200 mt-1">
              Live rows: {tel.live_telemetry_rows || 0} · Backfill: {tel.backfill_rows || 0}
            </li>
          </ul>
        </div>
      </div>

      {/* By purpose — where the money went */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
          Today's spend by purpose + model
        </h3>
        {byPurpose.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-xl py-6 text-center text-slate-400 text-xs">
            No calls logged today yet
          </div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="llm-cache-diag-purpose-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Purpose</th>
                  <th className="text-left px-3 py-2 font-semibold">Model</th>
                  <th className="text-right px-3 py-2 font-semibold">Calls</th>
                  <th className="text-right px-3 py-2 font-semibold">Cost (USD)</th>
                </tr>
              </thead>
              <tbody>
                {byPurpose.map((r, i) => (
                  <tr key={`${r.purpose || "?"}-${r.model || "?"}-${i}`} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono">{r.purpose || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-500 text-[11px]">{r.model || "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.calls}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold">${(r.cost_usd || 0).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Auto-reply source breakdown */}
      {autoReply.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            Auto-reply · last 24h by source
          </h3>
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="llm-cache-diag-source-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Source</th>
                  <th className="text-right px-3 py-2 font-semibold">Calls</th>
                  <th className="text-right px-3 py-2 font-semibold">Cache read</th>
                  <th className="text-right px-3 py-2 font-semibold">Cache write</th>
                  <th className="text-right px-3 py-2 font-semibold">Uncached</th>
                  <th className="text-right px-3 py-2 font-semibold">Cost (USD)</th>
                </tr>
              </thead>
              <tbody>
                {autoReply.map((r, i) => (
                  // scanner: SAFE — `r.source` is unique per row, falls back to idx only if missing. See /app/memory/SECURITY-NOTES.md#8
                  <tr key={r.source || i} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono font-semibold">{r.source}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.calls}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{(r.cache_read_tokens || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-violet-700">{(r.cache_creation_tokens || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-slate-500">{(r.uncached_input_tokens || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold">${(r.cost_usd || 0).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Iter 153 (Feb 2026, Yash) — Daily AI-reply cost history. Lets
          super-admins compare day-over-day costs at a glance. */}
      <LlmCostHistorySection />

      {/* Raw JSON expandable */}
      <details className="text-xs" data-testid="llm-cache-diag-raw">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-semibold py-1">
          View raw JSON
        </summary>
        <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 mt-2 overflow-auto text-[11px] leading-relaxed" style={{ maxHeight: 320 }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

/**
 * LlmCostHistorySection — Iter 153 (Feb 2026, Yash)
 *
 * Fetches per-IST-day LLM spend history from
 * `GET /api/admin/llm-cost-history` and renders a compact trend block
 * (totals + sparkline + table) so super-admins can quickly answer
 * "is today cheaper or more expensive than yesterday?" without leaving
 * the diag modal.
 */
const LlmCostHistorySection = () => {
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [hist, setHist] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async (d = days) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await reportsApi.getLlmCostHistory(d);
      setHist(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  const rowsAsc = hist?.rows ? [...hist.rows].reverse() : [];  // chronological for sparkline
  const maxCost = rowsAsc.reduce((m, r) => Math.max(m, r.total_cost_usd || 0), 0.0001);
  const totals = hist?.totals || {};
  const today = rowsAsc[rowsAsc.length - 1];
  const yesterday = rowsAsc[rowsAsc.length - 2];
  const delta = (today && yesterday)
    ? (today.total_cost_usd || 0) - (yesterday.total_cost_usd || 0)
    : null;
  const deltaPct = (yesterday && yesterday.total_cost_usd > 0 && delta !== null)
    ? (100 * delta / yesterday.total_cost_usd)
    : null;

  return (
    <div
      className="border border-slate-200 rounded-xl overflow-hidden"
      data-testid="llm-cost-history-section"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
            Daily AI cost history · IST
          </span>
          {delta !== null && yesterday && (
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-semibold font-mono ${
                delta <= 0
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                  : "bg-amber-100 text-amber-800 border border-amber-200"
              }`}
              title={`Today $${(today.total_cost_usd || 0).toFixed(2)} vs yesterday $${(yesterday.total_cost_usd || 0).toFixed(2)}`}
              data-testid="llm-cost-history-delta"
            >
              {delta <= 0 ? "▼" : "▲"} ${Math.abs(delta).toFixed(2)}
              {deltaPct !== null && ` (${deltaPct > 0 ? "+" : ""}${deltaPct.toFixed(0)}%)`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 14, 30].map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`text-[10px] font-bold px-2 py-1 rounded border transition-colors ${
                days === n
                  ? "bg-violet-600 text-white border-violet-600"
                  : "bg-white text-slate-600 border-slate-300 hover:border-violet-400"
              }`}
              data-testid={`llm-cost-history-range-${n}d`}
            >
              {n}d
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="px-4 py-6 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading daily history…
        </div>
      )}
      {err && !loading && (
        <div className="px-4 py-3 text-xs text-rose-700 bg-rose-50">
          Failed: {err}
        </div>
      )}

      {!loading && !err && hist && (
        <>
          {/* Totals row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-slate-200 bg-white" data-testid="llm-cost-history-totals">
            <div className="p-3 border-r border-slate-100">
              <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Total ({days}d)</p>
              <p className="text-lg font-extrabold font-mono mt-0.5">${(totals.total_cost_usd || 0).toFixed(2)}</p>
            </div>
            <div className="p-3 border-r border-slate-100">
              <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500">AI auto-reply</p>
              <p className="text-lg font-extrabold font-mono mt-0.5 text-violet-900">${(totals.ai_auto_reply_cost_usd || 0).toFixed(2)}</p>
            </div>
            <div className="p-3 border-r border-slate-100">
              <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Avg / day</p>
              <p className="text-lg font-extrabold font-mono mt-0.5">${(totals.avg_daily_cost_usd || 0).toFixed(2)}</p>
            </div>
            <div className="p-3 bg-teal-50">
              <p className="text-[10px] uppercase font-bold tracking-wider text-teal-700">Saved vs no-cache</p>
              <p className="text-lg font-extrabold font-mono mt-0.5 text-teal-900">${(totals.saved_usd_vs_no_cache || 0).toFixed(2)}</p>
            </div>
          </div>

          {/* Sparkline */}
          <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50">
            <div className="flex items-end gap-1 h-20" data-testid="llm-cost-history-sparkline">
              {rowsAsc.map((r) => {
                const heightPct = Math.max(2, ((r.total_cost_usd || 0) / maxCost) * 100);
                const isToday = r === today;
                return (
                  <div
                    key={r.date_ist}
                    className="flex-1 flex flex-col justify-end items-center group relative"
                    title={`${r.date_ist} — $${(r.total_cost_usd || 0).toFixed(4)} (${r.total_calls || 0} calls, cache hit ${r.cache_hit_pct || 0}%)`}
                  >
                    <div
                      className={`w-full rounded-t transition-all ${
                        isToday
                          ? "bg-violet-600"
                          : "bg-violet-300 group-hover:bg-violet-500"
                      }`}
                      style={{ height: `${heightPct}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-[9px] text-slate-400 font-mono">
              <span>{rowsAsc[0]?.date_ist}</span>
              <span>{rowsAsc[rowsAsc.length - 1]?.date_ist} (today)</span>
            </div>
          </div>

          {/* Compact table */}
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="llm-cost-history-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-slate-600 text-left">
                  <th className="px-3 py-1.5 font-semibold">Date (IST)</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Total $</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Auto-reply $</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Calls</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Cache hit</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Saved</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {hist.rows.map((r) => (
                  <tr key={r.date_ist} className="hover:bg-violet-50/40">
                    <td className="px-3 py-1.5">{r.date_ist}</td>
                    <td className="px-3 py-1.5 text-right font-bold">${(r.total_cost_usd || 0).toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-right text-violet-700">${(r.ai_auto_reply_cost_usd || 0).toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-600">{(r.total_calls || 0).toLocaleString()}</td>
                    <td className="px-3 py-1.5 text-right text-slate-600">{(r.cache_hit_pct || 0).toFixed(1)}%</td>
                    <td className="px-3 py-1.5 text-right text-teal-700 font-semibold">${(r.saved_usd || 0).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * JumpstartPrewarmerButton — Iter 152.22h
 * Fires one cache pre-warmer ping on demand. Useful:
 *   • Right before a busy WhatsApp session (e.g. 8:05am after the overnight sleep)
 *   • After a backend restart where the warmup loop may have drifted
 *   • To sanity-check the Anthropic pipeline end-to-end in <2s
 */
const JumpstartPrewarmerButton = () => {
  const [busy, setBusy] = useState(false);
  const fire = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const res = await reportsApi.fireNowPrewarmer();
      const u = res.data?.usage || {};
      if (res.data?.ok) {
        toast.success(
          `Pre-warmer fired · cache_read=${(u.cache_read_tokens || 0).toLocaleString()}, ` +
          `cache_write=${(u.cache_creation_tokens || 0).toLocaleString()}`
        );
      } else {
        toast.error(res.data?.error || "Jumpstart failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Jumpstart failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={fire}
      disabled={busy}
      className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md border border-current bg-white/50 hover:bg-white transition-colors disabled:opacity-60 flex items-center gap-1"
      data-testid="llm-cache-prewarmer-jumpstart-btn"
      title="Fire one pre-warmer ping right now (outside the normal 50-min schedule)"
    >
      {busy ? <Loader2 size={11} className="animate-spin" /> : <span>⚡</span>}
      {busy ? "Firing…" : "Jumpstart"}
    </button>
  );
};


/**
 * BudgetOverrideControls — Iter 152.22k / 152.22l
 *
 * Lets a super-admin set a custom AI budget cap for today, or fully
 * disable the killswitch. When an override is active, shows current
 * value + a Cancel button.
 *
 * Iter 152.22l (May 5, 2026, Yash) — added custom-amount input. The
 * preset buttons ($200, unlimited) are still here for one-click access,
 * but the user can now type any number in the input field.
 */
const BudgetOverrideControls = ({ today }) => {
  const [busy, setBusy] = useState(false);
  const [customCap, setCustomCap] = useState("");
  const overrideActive = today.override_active;

  const apply = async (payload, label) => {
    setBusy(true);
    try {
      await reportsApi.setBudgetOverride(payload);
      toast.success(label || "Budget override applied — AI auto-reply resumed");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const applyCustom = (e) => {
    e?.preventDefault?.();
    const n = parseFloat(customCap);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a number ≥ 0");
      return;
    }
    apply(
      { effective_cap_usd: n },
      n === 0
        ? "AI unlimited until IST midnight"
        : `Cap set to $${n.toFixed(0)} until IST midnight`,
    );
  };
  const cancel = async () => {
    if (!window.confirm("Cancel today's budget override and snap back to the base $50 cap?")) return;
    setBusy(true);
    try {
      await reportsApi.clearBudgetOverride();
      toast.success("Override cancelled — base cap restored");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (overrideActive) {
    const cap = today.override_cap_usd;
    const label = cap === 0 ? "Unlimited" : `$${(cap || 0).toFixed(0)}`;
    return (
      <div className="mt-2 space-y-1">
        <p className="text-[10px] text-violet-700 font-mono">
          Override active · cap: <span className="font-bold">{label}</span>
        </p>
        <button
          onClick={cancel}
          disabled={busy}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-violet-300 bg-white hover:bg-violet-50 text-violet-700 disabled:opacity-60 w-full"
          data-testid="llm-budget-cancel-override-btn"
        >
          {busy ? "…" : "Cancel override"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {/* Custom input — primary path now */}
      <form onSubmit={applyCustom} className="flex items-center gap-1">
        <span className="text-[10px] font-bold text-slate-600">$</span>
        <input
          type="number"
          step="1"
          min="0"
          value={customCap}
          onChange={(e) => setCustomCap(e.target.value)}
          placeholder="custom cap"
          className="flex-1 min-w-0 text-[11px] px-2 py-1 rounded border border-slate-300 focus:border-emerald-400 focus:ring-0 focus:outline-none bg-white font-mono"
          data-testid="llm-budget-custom-cap-input"
        />
        <button
          type="submit"
          disabled={busy || customCap === ""}
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-emerald-400 bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
          data-testid="llm-budget-apply-custom-btn"
          title="Set today's cap to this amount (auto-resets at IST midnight). Enter 0 for unlimited."
        >
          {busy ? "…" : "Apply"}
        </button>
      </form>
      <p className="text-[9px] text-slate-500 leading-tight">
        Resets at IST midnight · enter 0 for unlimited
      </p>
      {/* Quick presets */}
      <div className="flex gap-1">
        <button
          onClick={() => apply({ effective_cap_usd: 100 }, "Cap raised to $100")}
          disabled={busy}
          className="flex-1 text-[10px] px-1.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-60"
          data-testid="llm-budget-preset-100-btn"
        >
          +$100
        </button>
        <button
          onClick={() => apply({ effective_cap_usd: 200 }, "Cap raised to $200")}
          disabled={busy}
          className="flex-1 text-[10px] px-1.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-60"
          data-testid="llm-budget-preset-200-btn"
        >
          +$200
        </button>
        <button
          onClick={() => apply({ effective_cap_usd: 0 }, "AI unlimited until IST midnight")}
          disabled={busy}
          className="flex-1 text-[10px] px-1.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-60"
          data-testid="llm-budget-unlimited-btn"
        >
          ∞
        </button>
      </div>
    </div>
  );
};

